import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Phone, Navigation, Loader2, CalendarClock, MapPin, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { cachedGet } from "@/pages/tech/offline";
import { TechShell, StatusPill, techFileUrl, STATUS } from "@/pages/tech/ui";
import { JobActions } from "@/pages/tech/JobActions";
import { JobReport } from "@/pages/tech/JobReport";
import { JobBilling } from "@/pages/tech/JobBilling";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Info = ({ j }) => {
  const addr = [j.address, j.city, j.district, `${j.state || ""} ${j.pincode}`.trim()].filter(Boolean).join(", ");
  const rows = [["Service", j.service], ["Device type", j.device_type], ["Brand", j.brand], ["Model", j.model], ["Problem", j.description || "—"],
    ["Preferred time", `${j.preferred_date} · ${j.preferred_time}`], ["Booked on", fmt(j.created_at)], ["Area / PIN", `${j.city} · ${j.pincode}`]].filter((r) => r[1]);
  return (
    <div className="card-soft p-4 space-y-4" data-testid="tech-job-info">
      <div className="space-y-1">
        <p className="font-semibold flex items-center gap-2"><User className="h-4 w-4 text-primary" /><span data-testid="tech-job-customer">{j.name}</span></p>
        <p className="text-sm flex gap-2"><MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span data-testid="tech-job-address">{addr}</span></p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="rounded-full h-11" data-testid="tech-call-customer"><a href={`tel:+91${j.phone}`}><Phone className="h-4 w-4 mr-2" />Call {j.phone}</a></Button>
        <Button asChild className="rounded-full h-11" data-testid="tech-navigate"><a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`} target="_blank" rel="noopener noreferrer"><Navigation className="h-4 w-4 mr-2" />Navigate</a></Button>
      </div>
      <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-sm">{rows.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd>{v}</dd></div>)}</dl>
      {j.images?.length > 0 && <div className="flex gap-2 flex-wrap" data-testid="tech-customer-photos">{j.images.map((p) => <a key={p} href={techFileUrl(p)} target="_blank" rel="noopener noreferrer"><img src={techFileUrl(p)} alt="Customer upload" className="h-20 w-20 object-cover rounded-lg border border-border" /></a>)}</div>}
    </div>
  );
};

const Timeline = ({ items }) => items?.length > 0 && (
  <div className="card-soft p-4" data-testid="tech-job-timeline">
    <p className="font-semibold text-sm mb-2">Job history</p>
    <ol className="space-y-2 text-sm">{[...items].reverse().map((e, i) => <li key={i} className="flex justify-between gap-3"><span>{STATUS[e.status]?.[0] || e.status}{e.note ? ` — ${e.note}` : ""}</span><span className="text-xs text-muted-foreground whitespace-nowrap">{fmt(e.at)}</span></li>)}</ol>
  </div>
);

export default function TechJob() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: j, error, isLoading } = useQuery({ queryKey: ["tech", "job", id], queryFn: () => cachedGet(`/tech/jobs/${id}`), retry: false });
  useEffect(() => { document.title = `${j?.booking_no || "Job"} | Onkaal Technician`; }, [j]);
  return (
    <TechShell>
      <Link to="/tech" className="inline-flex items-center gap-1 text-sm text-primary mb-3" data-testid="tech-back"><ArrowLeft className="h-4 w-4" />All jobs</Link>
      {isLoading && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
      {error && <p className="card-soft p-6 text-sm text-destructive" data-testid="tech-job-error">{errMsg(error)}</p>}
      {j && (
        <div className="space-y-4" data-testid="tech-job-detail">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-2xl font-bold text-primary" data-testid="tech-job-no">{j.booking_no}</h1>
            <StatusPill s={j.status} />
          </div>
          {j.eta_at && j.status === "on_the_way" && <p className="text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />ETA shared with customer: {fmt(j.eta_at)}</p>}
          {j.customer_rating && <div className="card-soft p-4 text-sm" data-testid="tech-job-rating"><p className="font-semibold">Customer rating: <span className="text-amber-500">{"★".repeat(j.customer_rating.rating)}{"☆".repeat(5 - j.customer_rating.rating)}</span></p>{j.customer_rating.comment && <p className="text-muted-foreground mt-1">“{j.customer_rating.comment}”</p>}</div>}
          <JobActions job={j} onRejected={() => nav("/tech", { replace: true })} />
          <Info j={j} />
          {["arrived", "in_progress", "completed"].includes(j.status) && <JobReport key={j.report?.updated_at || "new"} job={j} locked={j.invoice?.paid > 0} />}
          {["in_progress", "completed"].includes(j.status) && <JobBilling job={j} />}
          <Timeline items={j.timeline} />
        </div>
      )}
    </TechShell>
  );
}
